import apiClient from './client';

export enum DeviceStatus {
  Available = 'Available',
  Assigned = 'Assigned',
  InRepair = 'InRepair',
  Retired = 'Retired',
  Lost = 'Lost',
  Scrapped = 'Scrapped',
}

export enum DeviceCondition {
  New = 'New',
  Good = 'Good',
  Fair = 'Fair',
  Poor = 'Poor',
  Broken = 'Broken',
}

export enum LocationType {
  Branch = 'Branch',
  Personnel = 'Personnel',
  Repair = 'Repair',
  Storage = 'Storage',
}

export interface CurrentLocation {
  type: LocationType;
  id?: string;
  name: string;
  since: string;
}

export interface Device {
  id: string;
  product: string | any;
  name: string;
  brand: string;
  model?: string;
  category: string | any;
  serialNumber?: string;
  assetTag?: string;
  purchaseDate: string;
  purchasePrice: number;
  warrantyEndDate?: string;
  status: DeviceStatus;
  condition: DeviceCondition;
  currentLocation: CurrentLocation;
  branch: string;
  createdBy?: string | any;
  specifications?: string;
  notes?: string;
  scrapPhotos?: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDeviceDto {
  product: string;
  hasSerialNumber?: boolean;
  serialNumber?: string;
  quantity?: number;
  assetTag?: string;
  purchaseDate: string;
  purchasePrice: number;
  warrantyEndDate?: string;
  condition?: DeviceCondition;
  specifications?: string;
  notes?: string;
}

export interface UpdateDeviceDto {
  serialNumber?: string;
  assetTag?: string;
  purchaseDate?: string;
  purchasePrice?: number;
  warrantyEndDate?: string;
  status?: DeviceStatus;
  condition?: DeviceCondition;
  specifications?: string;
  notes?: string;
  isActive?: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const devicesApi = {
  getAll: async (): Promise<Device[]> => {
    const response = await apiClient.get('/devices');
    // Backend now returns paginated response by default
    const data = response.data.data || response.data;
    if (Array.isArray(data)) {
      return data.map((item: any) => ({ ...item, id: item._id }));
    }
    return [];
  },

  getAllPaginated: async (page: number = 1, limit: number = 50, search?: string, product?: string, branch?: string, company?: string, status?: string, category?: string, department?: string): Promise<PaginatedResponse<Device>> => {
    const params: any = { page, limit };
    if (search) {
      params.search = search;
    }
    if (product && product !== 'all') {
      params.product = product;
    }
    if (branch && branch !== 'all') {
      params.branch = branch;
    }
    if (company && company !== 'all') {
      params.company = company;
    }
    if (status) {
      params.status = status;
    }
    if (category) {
      params.category = category;
    }
    if (department && department !== 'all') {
      params.department = department;
    }
    const response = await apiClient.get('/devices', { params });
    return {
      ...response.data,
      data: response.data.data.map((item: any) => {
        const device = { ...item, id: item._id };
        // Transform nested branch object if it exists and has _id
        if (device.branch && typeof device.branch === 'object' && device.branch._id) {
          device.branch = { ...device.branch, id: device.branch._id };
        }
        return device;
      }),
    };
  },

  getDashboardSummary: async (): Promise<any> => {
    const response = await apiClient.get('/devices/dashboard-summary');
    return response.data;
  },

  getScrapped: async (): Promise<Device[]> => {
    const response = await apiClient.get('/devices/scrapped');
    return response.data.map((item: any) => ({ ...item, id: item._id }));
  },

  getComputerReport: async (): Promise<any> => {
    const response = await apiClient.get('/devices/computer-report');
    return response.data;
  },

  getProblematic: async (): Promise<any> => {
    const response = await apiClient.get('/devices/problematic');
    return response.data;
  },

  getOne: async (id: string): Promise<Device> => {
    const response = await apiClient.get(`/devices/${id}`);
    return { ...response.data, id: response.data._id };
  },

  getBySerialNumber: async (serialNumber: string): Promise<Device> => {
    const response = await apiClient.get(`/devices/serial/${serialNumber}`);
    return { ...response.data, id: response.data._id };
  },

  getByStatus: async (status: DeviceStatus): Promise<Device[]> => {
    const response = await apiClient.get(`/devices/status/${status}`);
    return response.data.map((item: any) => ({ ...item, id: item._id }));
  },

  getByBranch: async (branchId: string): Promise<Device[]> => {
    const response = await apiClient.get(`/devices/branch/${branchId}`);
    return response.data.map((item: any) => ({ ...item, id: item._id }));
  },

  search: async (query: string): Promise<Device[]> => {
    const response = await apiClient.get('/devices/search', { params: { q: query } });
    return response.data.map((item: any) => ({ ...item, id: item._id }));
  },

  create: async (data: CreateDeviceDto): Promise<Device | Device[]> => {
    const response = await apiClient.post('/devices', data);
    // Backend returns array of devices
    if (Array.isArray(response.data)) {
      return response.data.map((item: any) => ({ ...item, id: item._id }));
    }
    return { ...response.data, id: response.data._id };
  },

  update: async (id: string, data: UpdateDeviceDto): Promise<Device> => {
    const response = await apiClient.put(`/devices/${id}`, data);
    return { ...response.data, id: response.data._id };
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/devices/${id}`);
  },

  changeLocation: async (id: string, toBranchId: string, notes?: string): Promise<Device> => {
    const response = await apiClient.post(`/devices/${id}/change-location`, {
      toBranchId,
      notes,
    });
    return { ...response.data, id: response.data._id };
  },

  exportPDF: async (search?: string, product?: string, branch?: string, company?: string): Promise<Blob> => {
    const params: any = {};
    if (search) {
      params.search = search;
    }
    if (product && product !== 'all') {
      params.product = product;
    }
    if (branch && branch !== 'all') {
      params.branch = branch;
    }
    if (company && company !== 'all') {
      params.company = company;
    }
    const response = await apiClient.get('/devices/export/pdf', { 
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  downloadImportTemplate: async (): Promise<Blob> => {
    const response = await apiClient.get('/devices/import/template', {
      responseType: 'blob',
    });
    return response.data;
  },

  importFromExcel: async (file: File): Promise<{ success: number; failed: number; errors: any[] }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/devices/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  importFromData: async (data: any[]): Promise<{ success: number; failed: number; errors: any[] }> => {
    const response = await apiClient.post('/devices/import/data', { data });
    return response.data;
  },
};
